<template>
    <div class="z-10">
        <PageComponent 
            :loader="loader" @showLoader="showLoader" @hideLoader="hideLoader"
            :addButtonLabel="addButtonLabel"
            @handleAddNew="addNewLedger"
            :searchFilters="searchFilters"
            :dropdownOptions="dropdownOptions"
            @handleDynamicOption="handleDynamicOption"
            @searchPage="searchChartOfAccounts"
            @resetFilters="resetFilters"
            @removeItem="removeItem"
            @removeSelectedItems="removeItems"
            @printList="printApplicationList"
            v-model:printModalVisible="printModalVisible"
            :printTitle="printTitle"
            :pdfUrl="pdfUrl"
            :addingRight="addingRight"
            :removingRight="removingRight"
            :rightsModule="rightsModule"
            :columns="tableColumns"
            :rows="chartOfAccountsList"
            :actions="actions"
            :idField="idField"
            @handleSelectionChange="handleSelectionChange"
            @handleActionClick="handleActionClick"
            :enableContextMenu=true
            @handleShowDetails="handleShowDetails"
            @handleOpenLink="handleOpenLink"
            :groupingParentKey=true
            :groupingKey=true
            :count="propCount"
            :currentPage="currentPage"
            :result="propArrLen"
            @loadPrev="loadPrev"
            @loadNext="loadNext"
            @firstPage="firstPage"
            @lastPage="lastPage"
            :showNextBtn="showNextBtn"
            :showPreviousBtn="showPreviousBtn"
        />
    </div>
    <MovableModal v-model:visible="depModalVisible" :title="title" :modal_top="modal_top" :modal_left="modal_left" :modal_width="modal_width"
        :loader="modal_loader" @showLoader="showModalLoader" @hideLoader="hideModalLoader" >
        <DynamicForm 
            :fields="formFields" :flex_basis="flex_basis" :flex_basis_percentage="flex_basis_percentage" 
            :displayButtons="displayButtons" @handleSubmit="saveLedger" @handleReset="handleReset"
        />
    </MovableModal>
</template>

<script>
import axios from "axios";
import { ref, computed, watch, onMounted, onBeforeMount } from 'vue';
import PageComponent from '@/components/PageComponent.vue';
import FilterBar from "@/components/FilterBar.vue";
import MovableModal from '@/components/MovableModal.vue';
import DynamicForm from '../NewDynamicForm.vue';
import { useStore } from "vuex";
import { useToast } from "vue-toastification";

export default{
    name: 'Chart_Of_Accounts',
    components:{
        PageComponent,MovableModal,DynamicForm, FilterBar
    },
    setup(){
        const store = useStore();
        const toast = useToast();
        const loader = ref('');
        const modal_loader = ref('none');
        const title = ref('Ledger Details');
        const addingRight = ref('Adding Posting Account');
        const rightsModule = ref('Accounts');
        const addButtonLabel = ref('New Ledger');
        const idField = 'ledger_id';
        const depModalVisible = ref(false);
        const chartOfAccountsList = ref([]);
        const allowedRights = computed(()=> store.state.userData.permissions);
        const patient_ledger_totals = ref(0);
        const patientLedger = ref(false);
        const vendor_ledger_totals = ref(0);
        const vendorLedger = ref(false);
        const customer_ledger_totals = ref(0);
        const customerLedger = ref(false);
        const tenant_ledger_totals = ref(0);
        const tenantLedger = ref(false);
        const member_ledger_totals = ref(0);
        const memberLedger = ref(false);
        const loan_ledger_totals = ref(0);
        const loanLedger = ref(false);
        const saving_ledger_totals = ref(0);
        const savingLedger = ref(false);
        const share_ledger_totals = ref(0);
        const shareLedger = ref(false);
        const merge_patients_setting = ref('No');
        const merge_vendors_setting = ref('No');
        const merge_debtors_setting = ref('No');
        const merge_tenants_setting = ref('No');
        const merge_members_setting = ref('Yes');
        const merge_loans_setting = ref('Yes');
        const merge_savings_setting = ref('Yes');
        const merge_shares_setting = ref('Yes');
        const depResults = ref([]);
        const depArrLen = ref(0);
        const depCount = ref(0);
        const pageCount = ref(0);
        const currentPage = ref(1);
        const showNextBtn = ref(false);
        const showPreviousBtn = ref(false);
        const flex_basis = ref('');
        const flex_basis_percentage = ref('');
        const displayButtons = ref(true);
        const errors = ref([]);
        const selectedIds = ref([]);
        const modal_top = ref('150px');
        const modal_left = ref('400px');
        const modal_width = ref('30vw');
        const isEditing = computed(()=> store.state.Ledgers.isEditing)
        const selectedLedger = computed(()=> store.state.Ledgers.selectedLedger);                      
        const tableColumns = ref([
            {type: "checkbox"},
            {label: "Code", key:"ledger_code"},
            {label: "Account Name", key:"ledger_name"},
            {label: "Type", key:"ledger_type"},
            {label: "Financial Statement", key:"financial_statement"},
            {label: " ", key:"statusIcon"},
            {label: "Balance", key: "formatted_balance", type:"number", txtColor: "textColor"},
        ])
        const actions = ref([
            {name: 'edit', icon: 'fa fa-edit', title: 'Edit Ledger', rightName: 'Editing Posting Account'},
            {name: 'view', icon: 'fa fa-file-pdf-o', title: 'View Ledger', rightName: 'View Ledger Statement'},
            {name: 'block', icon: 'fa fa-ban', title: 'Block/Unblock Ledger', rightName: 'Block Posting Account'},
            {name: 'delete', icon: 'fa fa-trash', title: 'Delete Ledger', rightName: 'Deleting Posting Account'},
        ])
        const companyID = computed(()=> store.state.userData.company_id);
        const userID = computed(()=> store.state.userData.user_id);
        const defaultSettings = computed(()=> store.state.userData.defaultSettings);
        const ledger_code_search = computed({
            get: () => store.state.Ledgers.ledger_code_search,
            set: (value) => store.commit('Ledgers/SET_SEARCH_FILTERS', {"ledger_code_search":value}),
        });
        const ledger_name_search = computed({
            get: () => store.state.Ledgers.ledger_name_search,
            set: (value) => store.commit('Ledgers/SET_SEARCH_FILTERS', {"ledger_name_search":value}),
        });
        const financial_statement_search = computed({
            get: () => store.state.Ledgers.financial_statement_search,
            set: (value) => store.commit('Ledgers/SET_SEARCH_FILTERS', {"financial_statement_search":value}),
        });
        const searchFilters = ref([
            {type:'text', placeholder:"Search Code...", value: ledger_code_search, width: 36},
            {type:'text', placeholder:"Search Name...", value: ledger_name_search, width:48,},
            {
                type:'dropdown', placeholder:"Financial Statement..", value: financial_statement_search, width:48,
                options: [{text:'Income Statement',value:'Income Statement'},{text:'Balance Sheet',value:'Balance Sheet'}]
            },
        ]);
        const handleSelectionChange = (ids) => {
            selectedIds.value = ids;
        };
        const fetchDefaultSettings = async() =>{
            for(let i=0; i < defaultSettings.value.length; i++){
                if(defaultSettings.value[i].setting_name === 'Merge Patients Ledgers in Reports'){
                    merge_patients_setting.value = defaultSettings.value[i].setting_value_name;
                }else if(defaultSettings.value[i].setting_name === 'Merge Debtors Ledgers in Reports'){
                    merge_debtors_setting.value = defaultSettings.value[i].setting_value_name;
                }else if(defaultSettings.value[i].setting_name === 'Merge Vendors Ledgers in Reports'){
                    merge_vendors_setting.value = defaultSettings.value[i].setting_value_name;
                }else if(defaultSettings.value[i].setting_name === 'Merge Tenants Ledgers in Reports'){
                    merge_tenants_setting.value = defaultSettings.value[i].setting_value_name;
                }else if(defaultSettings.value[i].setting_name === 'Merge Loans Ledgers in Reports'){
                    merge_loans_setting.value = defaultSettings.value[i].setting_value_name;
                }else if(defaultSettings.value[i].setting_name === 'Merge Savings Ledgers in Reports'){
                    merge_savings_setting.value = defaultSettings.value[i].setting_value_name;
                }else if(defaultSettings.value[i].setting_name === 'Merge Shares Ledgers in Reports'){
                    merge_shares_setting.value = defaultSettings.value[i].setting_value_name;
                }
            }
        };
        const formFields = ref([]);
        const updateFormFields = () => {
            formFields.value = [
                { type: 'text', name: 'ledger_code',label: "Code", value: selectedLedger.value?.ledger_code || '', required: true },
                { type: 'text', name: 'ledger_name',label: "Name", value: selectedLedger.value?.ledger_name || '', required: true },
                { type: 'dropdown', name: 'ledger_type',label: "Ledger Type", value: selectedLedger.value?.ledger_type || '', placeholder: "", required: true, options: [{ text: 'Cashbook', value: 'Cashbook' }, { text: 'Current Asset', value: 'Current Asset' }, { text: 'Fixed Asset', value: 'Fixed Asset' }, { text: 'Current Liability', value: 'Current Liability' }, { text: 'Longterm Liability', value: 'Longterm Liability' },{ text: 'Owner Equity', value: 'Owner Equity' },{ text: 'Income', value: 'Income' },{ text: 'Expenses', value: 'Expenses' }] },

            ];
        };

        watch([selectedLedger], () => {
            if(selectedLedger.value){
                updateFormFields();
            }        
        }, { immediate: true });

        const isDisabled =(permissionName) =>{
            const permission = allowedRights.value.find(p => p.rightName === permissionName);
            return permission ? !permission.right_status : true;
        };

        const addNewLedger = () =>{
            updateFormFields();
            depModalVisible.value = true;
            handleReset();
            store.dispatch("Ledgers/updateState",{isEditing:false, selectedLedger: null})
            flex_basis.value = '1/2';
            flex_basis_percentage.value = '50';
        }

        const handleReset = () =>{
            for(let i=0; i < formFields.value.length; i++){
                formFields.value[i].value = '';
            }
        }
        const showModalLoader = () =>{
            modal_loader.value = "block";
        }
        const hideModalLoader = () =>{
            modal_loader.value = "none";
        }
        const createLedger = async() =>{
            showModalLoader();
            let formData = {
                ledger_type: formFields.value[2].value,
                ledger_code: formFields.value[0].value,
                ledger_name: formFields.value[1].value,
                financial_statement: (formFields.value[2].value == "Income" || formFields.value[2].value == "Expenses") ? "Income Statement" : "Balance Sheet",
                company: companyID.value
            }
            errors.value = [];
            for(let i=0; i < formFields.value.length; i++){
                if(formFields.value[i].value =='' && formFields.value[i].required == true){
                    errors.value.push('Error');
                }
            }
            if(errors.value.length){
                toast.error('Fill In Required Fields');
                hideModalLoader();
            }else{
                try {
                    const response = await store.dispatch('Ledgers/createLedger', formData);
                    if(response && response.status === 201) {
                        hideModalLoader();
                        toast.success('Ledger created successfully!');
                        handleReset();
                    }else {
                        toast.error('An error occurred while creating the ledger.');
                    }
                } catch (error) {
                    console.error(error.message);
                    toast.error('Failed to create ledger: ' + error.message);
                } finally {
                    hideModalLoader();
                    searchChartOfAccounts();
                }
            }

        }
        const updateLedger = async() =>{
            showModalLoader();
            errors.value = [];
            let formData = {
                ledger_type: formFields.value[2].value,
                ledger_code: formFields.value[0].value,
                ledger_name: formFields.value[1].value,
                financial_statement: selectedLedger.value.financial_statement,
                ledger: selectedLedger.value.ledger_id,
                company: companyID.value
            }
            for(let i=0; i < formFields.value.length; i++){
                if(formFields.value[i].value =='' && formFields.value[i].required == true){
                    errors.value.push('Error');
                }
            }
            if(errors.value.length){
                    toast.error('Fill In Required Fields');
            }else{
                try {
                    const response = await store.dispatch('Ledgers/updateLedger', formData);
                    if (response && response.status === 200) {
                        hideModalLoader();
                        toast.success("Ledger updated successfully!");
                        handleReset();

                    } else {
                        toast.error('An error occurred while updating the Ledger.');
                    }
                } catch (error) {
                    console.error(error.message);
                    toast.error('Failed to update Ledger: ' + error.message);
                } finally {
                    hideModalLoader();
                    store.dispatch("Ledgers/updateState",{isEditing:false, selectedLedger: null})
                    searchChartOfAccounts();
                }
            }
        }
        const saveLedger = () =>{
            if(isEditing.value == true){
                updateLedger();
            }else{
                createLedger();
            }
        }

        const showLoader = () =>{
            loader.value = "block";
        }
        const hideLoader = () =>{
            loader.value = "none";
        }
        const ledgerColor = (ledgerTotal) =>{
            if(ledgerTotal >= 0){
                return 'black';
            }else{
                return 'red';
            }
        }
        const searchChartOfAccounts = () =>{
            showLoader();
            patient_ledger_totals.value = 0;
            vendor_ledger_totals.value = 0;
            customer_ledger_totals.value = 0;
            tenant_ledger_totals.value = 0;
            saving_ledger_totals.value = 0;
            share_ledger_totals.value = 0;
            loan_ledger_totals.value = 0;
            member_ledger_totals.value = 0;

            let formData = {
                ledger_code: ledger_code_search.value,
                ledger_name: ledger_name_search.value,
                financial_statement: financial_statement_search.value,
                company_id: companyID.value
            }
            axios
            .post(`api/v1/chart-of-accounts-search/?page=${currentPage.value}`,formData)
            .then((response)=>{
                for(let i = response.data.results.length - 1; i >= 0; i--){
                    if(response.data.results[i].ledger_category === "Patients" && merge_patients_setting.value === 'Yes'){
                        patient_ledger_totals.value += response.data.results[i].balance;
                        patientLedger.value = true;
                        response.data.results.splice(i, 1);
                    }else if(response.data.results[i].ledger_category === "Vendors" && merge_vendors_setting.value === 'Yes'){
                        vendor_ledger_totals.value += response.data.results[i].balance;
                        vendorLedger.value = true;
                        response.data.results.splice(i, 1);
                    }else if(response.data.results[i].ledger_category === "Debtors" && merge_debtors_setting.value === 'Yes'){
                        customer_ledger_totals.value += response.data.results[i].balance;
                        customerLedger.value = true;
                        response.data.results.splice(i, 1);
                    }else if(response.data.results[i].ledger_category === "Tenants" && merge_tenants_setting.value === 'Yes'){
                        tenant_ledger_totals.value += response.data.results[i].balance;
                        tenantLedger.value = true;
                        response.data.results.splice(i, 1);
                    }else if(response.data.results[i].ledger_category === "Members" && merge_members_setting.value === 'Yes'){
                        member_ledger_totals.value += response.data.results[i].balance;
                        memberLedger.value = true;
                        response.data.results.splice(i, 1);
                    }else if(response.data.results[i].ledger_category === "Loans" && merge_loans_setting.value === 'Yes'){
                        loan_ledger_totals.value += response.data.results[i].balance;
                        loanLedger.value = true;
                        response.data.results.splice(i, 1);
                    }else if(response.data.results[i].ledger_category === "Savings" && merge_savings_setting.value === 'Yes'){
                        saving_ledger_totals.value += response.data.results[i].balance;
                        savingLedger.value = true;
                        response.data.results.splice(i, 1);
                    }else if(response.data.results[i].ledger_category === "Shares" && merge_shares_setting.value === 'Yes'){
                        share_ledger_totals.value += response.data.results[i].balance;
                        shareLedger.value = true;
                        response.data.results.splice(i, 1);
                    }                 
                }

                if(response.data.results.length){
                    if(patientLedger.value){
                        let patientArr ={
                            "ledger_code": 'PAT',
                            "ledger_name": 'PATIENTS',
                            "ledger_type": 'Current Asset',
                            "ledger_category": 'Patients',
                            "financial_statement": 'Balance Sheet',
                            "balance": patient_ledger_totals.value,
                            "formatted_balance": patient_ledger_totals.value.toLocaleString(),
                            "status": 'Active',
                            "group_parent_category": "Asset",
                            "group_category": 'Current Asset',
                            "group_label": 'Ledger',
                            "textColor": ledgerColor(patient_ledger_totals.value),
                        }
                        response.data.results.push(patientArr);
                    }
                    if(customerLedger.value){
                        let customersArr ={
                            "ledger_code": 'DEB',
                            "ledger_name": 'DEBTORS',
                            "ledger_type": 'Current Asset',
                            "ledger_category": 'Debtors',
                            "financial_statement": 'Balance Sheet',
                            "balance": customer_ledger_totals.value,
                            "formatted_balance": customer_ledger_totals.value.toLocaleString(),
                            "status": 'Active',
                            "group_parent_category": "Asset",
                            "group_category": 'Current Asset',
                            "group_label": 'Ledger',
                            "textColor": ledgerColor(customer_ledger_totals.value),
                        }
                        response.data.results.push(customersArr);
                    }
                    if(vendorLedger.value){
                        let vendorsArr ={
                            "ledger_code": 'VEN',
                            "ledger_name": 'VENDORS',
                            "ledger_type": 'Current Liability',
                            "ledger_category": 'Vendors',
                            "financial_statement": 'Balance Sheet',
                            "balance": vendor_ledger_totals.value,
                            "formatted_balance": vendor_ledger_totals.value.toLocaleString(),
                            "status": 'Active',
                            "group_parent_category": "Liability",
                            "group_category": 'Current Liability',
                            "group_label": 'Ledger',
                            "textColor": ledgerColor(vendor_ledger_totals.value),
                        }
                        response.data.results.push(vendorsArr);
                    }
                    if(tenantLedger.value){
                        let tenantArr ={
                            "ledger_code": 'TNT',
                            "ledger_name": 'TENANTS',
                            "ledger_type": 'Current Asset',
                            "ledger_category": 'Tenants',
                            "financial_statement": 'Balance Sheet',
                            "balance": tenant_ledger_totals.value,
                            "formatted_balance": tenant_ledger_totals.value.toLocaleString(),
                            "status": 'Active',
                            "group_parent_category": "Asset",
                            "group_category": 'Current Asset',
                            "group_label": 'Ledger',
                            "textColor": ledgerColor(tenant_ledger_totals.value),
                        }
                        response.data.results.push(tenantArr);
                    }
                    if(memberLedger.value){
                        let memberArr ={
                            "ledger_code": 'MEM',
                            "ledger_name": 'MEMBERS',
                            "ledger_type": 'Current Asset',
                            "ledger_category": 'Members',
                            "financial_statement": 'Balance Sheet',
                            "balance": member_ledger_totals.value,
                            "formatted_balance": member_ledger_totals.value.toLocaleString(),
                            "status": 'Active',
                            "group_parent_category": "Asset",
                            "group_category": 'Current Asset',
                            "group_label": 'Ledger',
                            "textColor": ledgerColor(member_ledger_totals.value),
                        }
                        response.data.results.push(memberArr);
                    }
                    if(loanLedger.value){
                        let loanArr ={
                            "ledger_code": 'LOAN',
                            "ledger_name": 'LOANS',
                            "ledger_type": 'Current Asset',
                            "ledger_category": 'Loans',
                            "financial_statement": 'Balance Sheet',
                            "balance": loan_ledger_totals.value,
                            "formatted_balance": loan_ledger_totals.value.toLocaleString(),
                            "status": 'Active',
                            "group_parent_category": "Asset",
                            "group_category": 'Current Asset',
                            "group_label": 'Ledger',
                            "textColor": ledgerColor(loan_ledger_totals.value),
                        }
                        response.data.results.push(loanArr);
                    }
                    if(savingLedger.value){
                        let savingArr ={
                            "ledger_code": 'SAV',
                            "ledger_name": 'SAVINGS',
                            "ledger_type": 'Current Liability',
                            "ledger_category": 'Savings',
                            "financial_statement": 'Balance Sheet',
                            "balance": saving_ledger_totals.value,
                            "formatted_balance": saving_ledger_totals.value.toLocaleString(),
                            "status": 'Active',
                            "group_parent_category": "Liability",
                            "group_category": 'Current Liability',
                            "group_label": 'Ledger',
                            "textColor": ledgerColor(saving_ledger_totals.value),
                        }
                        response.data.results.push(savingArr);
                    }
                    if(shareLedger.value){
                        let shareArr ={
                            "ledger_code": 'SHA',
                            "ledger_name": 'SHARES',
                            "ledger_type": 'Owner Equity',
                            "ledger_category": 'Shares',
                            "financial_statement": 'Balance Sheet',
                            "balance": share_ledger_totals.value,
                            "formatted_balance": share_ledger_totals.value.toLocaleString(),
                            "status": 'Active',
                            "group_parent_category": "Equity",
                            "group_category": 'Owner Equity',
                            "group_label": 'Ledger',
                            "textColor": ledgerColor(share_ledger_totals.value),
                        }
                        response.data.results.push(shareArr);
                    }
                }
                chartOfAccountsList.value = response.data.results;

            })
            .catch((error)=>{
                console.log(error);
            })
            .finally(()=>{
                hideLoader();
            })
        }
        const loadPrev = () =>{
            if (currentPage.value <= 1){
                currentPage.value = 1;
            }else{
                currentPage.value -= 1;
            }
            
            searchChartOfAccounts();
        }
        const loadNext = () =>{
            if(currentPage.value >= pageCount.value){
                currentPage.value = pageCount.value;
            }else if(currentPage.value < pageCount.value){
                currentPage.value += 1;
            }
            
            searchChartOfAccounts();
        }
        const firstPage = ()=>{
            currentPage.value = 1;
            searchChartOfAccounts();
        }
        const lastPage = () =>{
            currentPage.value = pageCount.value;
            searchChartOfAccounts();
        }
        const resetFilters = () =>{
            store.commit('Ledgers/RESET_SEARCH_FILTERS')
            searchChartOfAccounts();
        };
        const handleActionClick = async(rowIndex, action, row) =>{
            if( action == 'edit'){
                let formData = {
                    ledger: row[idField],
                    company: companyID.value,
                }
                await store.dispatch('Ledgers/updateState', {selectedLedger: null,isEditing: false});
                await store.dispatch('Ledgers/fetchLedger', formData);
                depModalVisible.value = true;
                flex_basis.value = '1/2';
                flex_basis_percentage.value = '50'; 
                
            }else if(action == 'delete'){
                const ledgerID = [row[idField]];
                let formData = {
                    company: companyID.value,
                    ledger: ledgerID
                }
                await store.dispatch('Ledgers/deleteLedger', formData);

            }else if(action == 'view'){
                let formData1 = {
                    ledger: row[idField],
                    company: companyID.value,
                }
                store.commit('pageTab/ADD_PAGE', {'FA':'Ledger_Details'});
                store.state.pageTab.faActiveTab = 'Ledger_Details'; 
                await store.dispatch('Ledgers/fetchLedger', formData1)
                await store.dispatch('Ledgers/updateState', {ledgerID: row[idField], ledgerRunningBalance:0 })
            }else if(action == 'block'){
                let ledgerStatus = row['status'];
                if(ledgerStatus == 'Inactive'){
                    ledgerStatus = 'Active';
                }else{
                    ledgerStatus = 'Inactive';
                }
                showModalLoader();
                let formData = {
                    ledger_type: row['ledger_type'],
                    ledger_code: row['ledger_code'],
                    ledger_name: row['ledger_name'],
                    financial_statement: row['financial_statement'],
                    ledger: row['ledger_id'],
                    status: ledgerStatus,
                    company: companyID.value
                }
                try {
                    const response = await store.dispatch('Ledgers/updateLedger', formData);
                    if (response && response.status === 200) {
                        hideModalLoader();
                        toast.success("Success!");
                        handleReset();
                    } else {
                        toast.error('An error occurred.');
                    }
                } catch (error) {
                    toast.error('Failed to block/unblock Ledger: ' + error.message);
                } finally {
                    hideModalLoader();
                    searchChartOfAccounts();
                }
                
            }
        };
        const dropdownOptions = ref([
            {label: 'Populate Default', action: 'populate-default', icon: 'fa-magic', colorClass: 'text-yellow-600', rightName: 'Adding Posting Account'},
        ]);
        const handleDynamicOption = async(option) =>{
            if(option == 'populate-default'){
                showLoader();
                let formData = {
                    company: companyID.value,
                }
                await axios.post('api/v1/autopopulate-ledgers/',formData).
                then((response)=>{
                    if(response.data.msg == "Success"){
                        toast.success("Success!")
                        searchChartOfAccounts();
                    }else{
                        toast.error(response.data.msg)
                    }
                })
                .catch((error)=>{
                    toast.error(error.message)
                })
                .finally(()=>{
                    hideLoader();
                })
            }
        };
        const handleShowDetails = async(row) =>{
            let formData1 = {
                ledger: row[idField],
                company: companyID.value,
            }
            store.commit('pageTab/ADD_PAGE', {'FA':'Ledger_Details'});
            store.state.pageTab.faActiveTab = 'Ledger_Details'; 
            await store.dispatch('Ledgers/fetchLedger', formData1)
            await store.dispatch('Ledgers/updateState', {ledgerID: row[idField], ledgerRunningBalance:0 })
        };
        onBeforeMount(()=>{
            fetchDefaultSettings(); 
            allowedRights.value = store.state.userData.permissions;     
        });
        onMounted(()=>{
            searchChartOfAccounts();
        })
        return{
            title,idField, searchChartOfAccounts, addButtonLabel, searchFilters, resetFilters, tableColumns, chartOfAccountsList,
            depResults, depArrLen, depCount, pageCount, showNextBtn, showPreviousBtn,modal_top, modal_left, modal_width,
            loadPrev, loadNext, firstPage, lastPage, actions, formFields, depModalVisible, addNewLedger,dropdownOptions, handleDynamicOption,
            displayButtons,flex_basis,flex_basis_percentage, handleReset, saveLedger,handleActionClick,handleSelectionChange,
            showLoader, loader, hideLoader, modal_loader, showModalLoader, hideModalLoader,handleShowDetails,
            addingRight,rightsModule
        }
    }
}
</script>

<style scoped>
.disabled {
  opacity: 0.5;
  pointer-events: none;
}
.table-container thead th {
  position: sticky;
  top: 0;
  background: #3b4252;
}
.chart-of-accounts-table{
    min-height: 50vh;
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}
</style>